import Row from "antd/lib/row"
import React, { Ref, RefObject } from "react"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import Button from "components/Button/Button/Index"
import { Buttons } from "components/Card/Card"
import { GenericModal } from "components/Modal/Modal"
import { languages } from "lib/languages"
import { Call } from "types/payload"
import { ModalProps } from "types/props"
import { ClientStore, DocumentsStore } from "types/store"

import { actions } from "./actions"
import { DOCUMENT_NOT_FOUND_PAGE } from "core/constants"

export const hasDocuments = (call: Call): boolean => {
  if (
    !call.is_prescription &&
    !call.is_report &&
    !call.is_workleave &&
    !call.is_certificate
  ) {
    return false
  }
  return true
}

export const ReadableDocument = React.forwardRef(
  (
    props: { documentUrl?: string, setPrinted?: (arg: boolean) => void, isPrinted: boolean },
    ref: Ref<HTMLIFrameElement>
  ): JSX.Element => (
    props.documentUrl ? (
      <iframe
        title="readable-document"
        className="iframe-document"
        id="iFrame"
        name="iFrame"
        src={`${props.documentUrl}#toolbar=0`}
        ref={ref}
        onLoad={(win: any) => {
          try {
            if (props.isPrinted || props.documentUrl === DOCUMENT_NOT_FOUND_PAGE) return;
            win.target?.contentWindow?.print();
            props.setPrinted?.(true);
          } catch (error) {
            console.error(error, {
              path: 'ReadableDocument::onLoad::print',
            })
          }
        }}
      />
    ) : (
      <div className="iframe-document"></div>
    )
  )
)

const handlePrintDocument = (
  iframeRef: RefObject<HTMLIFrameElement>,
  setLoading: (boolean: boolean) => void,
  setPrinted: (boolean: boolean) => void,
) => {
  setLoading(false)
  const ifrm = iframeRef?.current as HTMLIFrameElement
  const win: Window | null = ifrm?.contentWindow || null
  const doc: Document | null = ifrm?.contentDocument || null
  // if the pdf takes up space in the body, we print
  // By default , body empty has a length equal to 13 in my case
  const src = iframeRef.current?.getAttribute("src") || "";
  // Split is needed because we append #toolbar=0
  const baseSrc = src.split("#")[0];
  console.log("handlePrintDocument L70", baseSrc)
  if (
    win &&
    typeof win.onload !== "undefined" &&
    baseSrc &&
    baseSrc !== DOCUMENT_NOT_FOUND_PAGE
  ) {
    win.print()
    setPrinted(true);
    setTimeout(() => {
      setLoading(true)
    }, 1000)
  } else {
    console.error("Could'nt print document", {
      path: 'document::handlePrintDocument'
    })
    if(!doc) return;
    setTimeout(() => {
      handlePrintDocument(iframeRef, setLoading, setPrinted);
    }, 1000)
  }
}

export const ModalDocument = ({
  call,
  ...rest
}: Partial<ModalProps> & { call?: Call }): JSX.Element => {
  const dispatch = useDispatch()
  const { documentUrl, callId, ...props } = useSelector(
    ({
      client,
      documents,
    }: {
      client: ClientStore
      documents: DocumentsStore
    }) => ({
      documentUrl: call && client.documentUrl,
      callId: client.call?.id || -1,
      ...documents,
    })
  )
  const [visible, setVisible] = useState<boolean>(!!call)
  const [loading, setLoading] = useState<boolean>(false)
  const [showConfirmSendingDocuments, setShowConfirmSendingDocuments] =
    useState<boolean>(false)

  const [printed, setPrinted] = useState<boolean>(false)
  const [printedModal, setPrintedModal] = useState<boolean>(false)
  const [printInterval, setPrintInterval] = useState<any>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        console.log("print interval Document URL ::::", documentUrl)
        if (documentUrl && documentUrl !== DOCUMENT_NOT_FOUND_PAGE) {
          iframeRef?.current?.contentWindow?.print()
          setPrinted(true)
        }
      } catch (error) {
        console.error(error, {
          path: 'document::interval::print',
        })
      }
    }, 5000);
    setPrintInterval(interval);

    return () => clearInterval(interval);
  }, [])

  useEffect(() => {
    // if normal print behavior didn't work, try to print anyway after 5 seconds
    if (printed) {
      clearInterval(printInterval);
    }
    return () => clearInterval(printInterval);

  }, [printed, printInterval])

  useEffect(() => {
    setVisible(!!call)
    if (call) {
      handlePrintDocument(iframeRef, setLoading, setPrinted)
      dispatch(actions.request(call.id))
    } else {
      setLoading(true)
    }
  }, [call])

    // Reset "printed" whenever a new document URL comes in
  useEffect(() => {
    if (documentUrl) {
      setPrinted(false)
    }
  }, [documentUrl])
  // Cleanup documentUrl when modal closes
  useEffect(() => {
    if (!visible && documentUrl) {
      URL.revokeObjectURL(documentUrl)
      dispatch(actions.success(undefined)) // clear redux state
      setPrinted(false)
      setLoading(false)
    }
  }, [visible, documentUrl, dispatch])

  const handleSendDocumentsToPharmacy = () => {
    dispatch(actions.sendDocumentsRequest({ id: callId }))
  }
  const ConfirmSendingDocuments = (props: any) => (
    <div>
      <Row justify="center">
        <h2 className="primary-color">
          <b>{languages.confirmTheSendingOfYourDocuments}</b>
        </h2>
        <Buttons style={{ paddingBottom: "100px" }}>
          <Button
            wide="long"
            loading={props.loading}
            disabled={props.loading}
            onClick={() => {
              handleSendDocumentsToPharmacy()
              setShowConfirmSendingDocuments(false)
            }}
          >
            {languages.yes}
          </Button>
          <Button
            wide="long"
            onClick={() => setShowConfirmSendingDocuments(false)}
          >
            {languages.no}
          </Button>
        </Buttons>
      </Row>
      <p
        className="primary-color"
        style={{ position: "absolute", bottom: "-20px" }}
      >
        *{languages.referrerIsAHealthProfessional}
      </p>
    </div>
  )

  const Actions = (props: any) => (
    <>
      <Buttons>
        <Button
          loading={props.loading}
          type="primary"
          disabled={props.loading}
          onClick={() => {
            if (printed) {
              setPrintedModal(true)
            } else {
              handlePrintDocument(iframeRef, setLoading, setPrinted)
            }
          }}
        >
          {printed ? languages.rePrint : languages.print}
        </Button>
        <Button
          loading={props.loading}
          disabled={props.loading || !props.canSendEmail}
          onClick={() => setShowConfirmSendingDocuments(true)}
        >
          {props.canSendEmail
            ? languages.sendDocumentsToTheReferrer
            : languages.sent}
        </Button>
        <Button
          danger
          type="primary"
          loading={props.loading}
          disabled={props.loading}
          onClick={rest.onAccept}
        >
          {languages.finishAndDisconnect}
        </Button>
      </Buttons>
      <GenericModal
        visible={printedModal}
        title={languages.rePrintTitle}
        contentText={languages.rePrintContent}
        acceptText={languages.no}
        acceptButtonProps={{ danger: false, type: "default" }}
        onAccept={() => setPrintedModal(false)}
        cancelText={languages.yes}
        cancelButtonProps={{ danger: true, type: "primary" }}
        onCancel={() => {
          setPrintedModal(false)
          handlePrintDocument(iframeRef, setLoading, setPrinted)
        }}
      />
    </>
  )
  return (
    <GenericModal
      {...rest}
      title={languages.yourDocuments}
      loading={loading}
      footer={
        rest.footer ||
        (!showConfirmSendingDocuments ? (
          <Actions {...props} />
        ) : (
          <ConfirmSendingDocuments {...props} />
        ))
      }
      visible={visible}
    >
      {!showConfirmSendingDocuments ? (
        <ReadableDocument documentUrl={documentUrl} ref={iframeRef} setPrinted={(isPrinted) => setPrinted(isPrinted)} isPrinted={printed} />
      ) : (
        <></>
      )}
    </GenericModal>
  )
}
